import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  Users, AlertCircle, Calendar, CheckCircle, ListTodo, Clock,
  Star, Activity, FolderKanban, Wallet, TrendingDown, TrendingUp, RefreshCw
} from "lucide-react";
import { Button } from "@/components/ui/button";
import BestPerformerCard from "./BestPerformerCard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EmployeeDashboard = ({ employeeMetrics, currentUser, businessId }) => {
  const [salaryPreview, setSalaryPreview] = useState(null);
  const [loadingSalary, setLoadingSalary] = useState(true);

  // Filter for "My Salary Preview" — "this" | "last" | "range"
  const today = new Date();
  const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const monthStart = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
  const monthEnd   = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
  const [filterMode, setFilterMode] = useState("this");
  const [rangeStart, setRangeStart] = useState(ymd(monthStart(today)));
  const [rangeEnd, setRangeEnd]     = useState(ymd(today));

  useEffect(() => {
    fetchSalaryPreview();
  }, [filterMode, rangeStart, rangeEnd]); // eslint-disable-line

  // Cross-tab sync: refetch when any HRMS module dispatches `hrms:data-changed`.
  // Lets admin edits in another tab (attendance, leave, salary) flow into this widget
  // without requiring the employee to reload.
  useEffect(() => {
    const onChange = (e) => {
      const kind = e?.detail?.kind;
      if (!kind || ["attendance", "leave", "salary"].includes(kind)) fetchSalaryPreview();
    };
    const onFocus = () => fetchSalaryPreview();
    window.addEventListener("hrms:data-changed", onChange);
    window.addEventListener("focus", onFocus);
    return () => {
      window.removeEventListener("hrms:data-changed", onChange);
      window.removeEventListener("focus", onFocus);
    };
  }, []); // eslint-disable-line

  const fetchSalaryPreview = async () => {
    setLoadingSalary(true);
    try {
      let qs = "";
      if (filterMode === "this") {
        qs = `?month=${today.getMonth() + 1}&year=${today.getFullYear()}`;
      } else if (filterMode === "last") {
        const lm = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        qs = `?month=${lm.getMonth() + 1}&year=${lm.getFullYear()}`;
      } else if (filterMode === "range" && rangeStart && rangeEnd && rangeStart <= rangeEnd) {
        qs = `?start_date=${rangeStart}&end_date=${rangeEnd}`;
      }
      const response = await fetch(`${BACKEND_URL}/api/attendance/my-salary-preview${qs}`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setSalaryPreview(data);
      }
    } catch (error) {
      console.error("Error fetching salary preview:", error);
    } finally {
      setLoadingSalary(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  if (!employeeMetrics) return null;

  const performance = employeeMetrics.performance || {};
  const breakdown = performance.breakdown || {};

  return (
    <div className="space-y-5">
      {/* Overview heading — mirrors AdminDashboard for a consistent top-of-page anchor */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            Overview
            {employeeMetrics.current_month && (
              <span className="text-xs font-normal text-slate-500 px-2 py-0.5 bg-slate-100 dark:bg-slate-800 rounded-full">
                {employeeMetrics.current_month}
              </span>
            )}
          </h2>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Hi {employeeMetrics.user_name || currentUser?.name} — your productivity snapshot
          </p>
        </div>
      </div>

      {/* Employee Welcome Section */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-6 text-white">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center">
            <span className="text-2xl font-bold">{currentUser?.name?.charAt(0).toUpperCase()}</span>
          </div>
          <div>
            <h2 className="text-xl font-bold">Welcome, {employeeMetrics.user_name || currentUser?.name}!</h2>
            <p className="text-indigo-100 text-sm">Here's your productivity overview for {employeeMetrics.current_month}</p>
          </div>
        </div>
      </div>

      {/* Employee Metric Cards - 3x2 Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* My To-Do Tasks */}
        <Card
          data-testid="employee-todo-card"
          className="bg-gradient-to-br from-slate-500 to-slate-600 text-white rounded-2xl shadow-lg shadow-slate-500/20 border border-white/10 hover:-translate-y-1 transition-all duration-300"
        >
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-white/80 font-medium">My To-Do Tasks</p>
                <h3 className="text-3xl font-heading font-bold mt-2">
                  {employeeMetrics.my_todo_tasks || 0}
                </h3>
                <p className="text-xs text-white/60 mt-1">Assigned & pending</p>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <ListTodo className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* In Progress */}
        <Card
          data-testid="employee-inprogress-card"
          className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white rounded-2xl shadow-lg shadow-blue-500/20 border border-white/10 hover:-translate-y-1 transition-all duration-300"
        >
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-white/80 font-medium">In Progress</p>
                <h3 className="text-3xl font-heading font-bold mt-2">
                  {employeeMetrics.my_in_progress || 0}
                </h3>
                <p className="text-xs text-white/60 mt-1">Currently working on</p>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Today's Completed */}
        <Card
          data-testid="employee-today-completed-card"
          className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white rounded-2xl shadow-lg shadow-emerald-500/20 border border-white/10 hover:-translate-y-1 transition-all duration-300"
        >
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-white/80 font-medium">Today's Completed</p>
                <h3 className="text-3xl font-heading font-bold mt-2">
                  {employeeMetrics.today_completed || 0}
                </h3>
                <p className="text-xs text-white/60 mt-1">Tasks done today</p>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Total Overdue */}
        <Card
          data-testid="employee-overdue-card"
          className="bg-gradient-to-br from-red-500 to-rose-600 text-white rounded-2xl shadow-lg shadow-red-500/20 border border-white/10 hover:-translate-y-1 transition-all duration-300"
        >
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-white/80 font-medium">Overdue Tasks</p>
                <h3 className="text-3xl font-heading font-bold mt-2">
                  {employeeMetrics.total_overdue || 0}
                </h3>
                <p className="text-xs text-white/60 mt-1">Past due date</p>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Monthly Completed */}
        <Card
          data-testid="employee-monthly-completed-card"
          className="bg-gradient-to-br from-violet-500 to-purple-600 text-white rounded-2xl shadow-lg shadow-violet-500/20 border border-white/10 hover:-translate-y-1 transition-all duration-300"
        >
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-white/80 font-medium">Monthly Completed</p>
                <h3 className="text-3xl font-heading font-bold mt-2">
                  {employeeMetrics.monthly_completed || 0}
                </h3>
                <p className="text-xs text-white/60 mt-1">Tasks this month</p>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <Calendar className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Performance Score Card */}
        <Card
          data-testid="employee-performance-card"
          className="bg-gradient-to-br from-amber-500 to-orange-600 text-white rounded-2xl shadow-lg shadow-amber-500/20 border border-white/10 hover:-translate-y-1 transition-all duration-300"
        >
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-white/80 font-medium">Performance Score</p>
                <h3 className="text-3xl font-heading font-bold mt-2">
                  {performance.score || 0}
                  <span className="text-lg">/100</span>
                </h3>
                <p className="text-xs text-white/60 mt-1">{performance.level || "N/A"}</p>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <Star className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ============ BEST PERFORMER OF THE MONTH ============ */}
      <BestPerformerCard businessId={businessId} />

      {/* ============ MY SALARY PREVIEW ============ */}
      <Card className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-sm" data-testid="employee-salary-preview">
        <CardHeader className="pb-2">
          <CardTitle className="flex flex-wrap items-center gap-2 text-lg font-semibold">
            <Wallet className="w-5 h-5 text-emerald-600" />
            <span>
              My Salary Preview
              {" — "}
              {salaryPreview?.is_range
                ? `${salaryPreview.date_range_start} → ${salaryPreview.date_range_end}`
                : salaryPreview
                  ? `${new Date(0, salaryPreview.month - 1).toLocaleString('default', { month: 'long' })} ${salaryPreview.year}`
                  : ""}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchSalaryPreview}
              disabled={loadingSalary}
              className="ml-auto h-7 w-7 p-0"
              title="Refresh"
              data-testid="refresh-my-salary"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loadingSalary ? "animate-spin" : ""}`} />
            </Button>
          </CardTitle>

          {/* Filter chips + date inputs */}
          <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
            {[
              { key: "this", label: "This Month" },
              { key: "last", label: "Last Month" },
              { key: "range", label: "Custom Range" },
            ].map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => setFilterMode(opt.key)}
                className={`px-3 py-1 rounded-full border text-xs font-medium transition-colors ${
                  filterMode === opt.key
                    ? "bg-emerald-600 text-white border-emerald-600"
                    : "bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600"
                }`}
                data-testid={`salary-filter-${opt.key}`}
              >
                {opt.label}
              </button>
            ))}
            {filterMode === "range" && (
              <div className="flex items-center gap-2 ml-1">
                <input
                  type="date"
                  value={rangeStart}
                  max={rangeEnd}
                  onChange={(e) => setRangeStart(e.target.value)}
                  className="h-8 px-2 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-xs"
                  data-testid="salary-range-start"
                />
                <span className="text-slate-400">→</span>
                <input
                  type="date"
                  value={rangeEnd}
                  min={rangeStart}
                  onChange={(e) => setRangeEnd(e.target.value)}
                  className="h-8 px-2 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-xs"
                  data-testid="salary-range-end"
                />
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="pt-4">
          {loadingSalary ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
            </div>
          ) : salaryPreview ? (
            <div className="space-y-4">
              {/* Working Days & Attendance Summary */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg text-center">
                  <p className="text-xl font-bold text-slate-800 dark:text-white">{salaryPreview.working_days}</p>
                  <p className="text-xs text-slate-500">Working Days</p>
                </div>
                <div className="p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg text-center">
                  <p className="text-xl font-bold text-emerald-600">{salaryPreview.present_days}</p>
                  <p className="text-xs text-slate-500">Present</p>
                </div>
                <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg text-center">
                  <p className="text-xl font-bold text-amber-600">{salaryPreview.half_days}</p>
                  <p className="text-xs text-slate-500">Half Days</p>
                </div>
                <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg text-center">
                  <p className="text-xl font-bold text-red-600">{salaryPreview.absent_days}</p>
                  <p className="text-xs text-slate-500">Absent</p>
                </div>
              </div>

              {/* Late & Overtime */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <TrendingDown className="w-4 h-4 text-orange-600" />
                    <span className="text-sm text-slate-600 dark:text-slate-400">Late Marks</span>
                  </div>
                  <span className="font-bold text-orange-600">{salaryPreview.late_count}</span>
                </div>
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-blue-600" />
                    <span className="text-sm text-slate-600 dark:text-slate-400">Overtime Hours</span>
                  </div>
                  <span className="font-bold text-blue-600">{salaryPreview.overtime_hours || 0}h</span>
                </div>
              </div>

              {/* Salary Breakdown */}
              <div className="border-t border-slate-200 dark:border-slate-700 pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600 dark:text-slate-400">Gross Salary</span>
                  <span className="font-medium">{formatCurrency(salaryPreview.gross_salary)}</span>
                </div>
                {salaryPreview.overtime_amount > 0 && (
                  <div className="flex justify-between text-sm text-blue-600">
                    <span>+ Overtime</span>
                    <span>+{formatCurrency(salaryPreview.overtime_amount)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm text-red-600">
                  <span>- Deductions</span>
                  <span>-{formatCurrency(salaryPreview.total_deductions)}</span>
                </div>
                <div className="flex justify-between text-lg font-bold pt-2 border-t border-slate-200 dark:border-slate-700">
                  <span className="text-slate-800 dark:text-white">Estimated Net Salary</span>
                  <span className="text-emerald-600">{formatCurrency(salaryPreview.net_payable)}</span>
                </div>
              </div>

              {/* Deduction Details (Collapsible) */}
              {salaryPreview.total_deductions > 0 && (
                <details className="bg-red-50 dark:bg-red-900/10 rounded-lg p-3">
                  <summary className="text-sm font-medium text-red-700 dark:text-red-400 cursor-pointer">
                    View Deduction Breakdown
                  </summary>
                  <div className="mt-2 space-y-1 text-sm text-red-600 dark:text-red-400">
                    {salaryPreview.absent_deduction > 0 && (
                      <div className="flex justify-between">
                        <span>Absent ({salaryPreview.absent_days} days)</span>
                        <span>-{formatCurrency(salaryPreview.absent_deduction)}</span>
                      </div>
                    )}
                    {salaryPreview.half_day_deduction > 0 && (
                      <div className="flex justify-between">
                        <span>Half Days ({salaryPreview.half_days})</span>
                        <span>-{formatCurrency(salaryPreview.half_day_deduction)}</span>
                      </div>
                    )}
                    {salaryPreview.late_deduction > 0 && (
                      <div className="flex justify-between">
                        <span>Late Marks ({salaryPreview.late_count})</span>
                        <span>-{formatCurrency(salaryPreview.late_deduction)}</span>
                      </div>
                    )}
                    {salaryPreview.pf_deduction > 0 && (
                      <div className="flex justify-between">
                        <span>PF</span>
                        <span>-{formatCurrency(salaryPreview.pf_deduction)}</span>
                      </div>
                    )}
                    {salaryPreview.esi_deduction > 0 && (
                      <div className="flex justify-between">
                        <span>ESI</span>
                        <span>-{formatCurrency(salaryPreview.esi_deduction)}</span>
                      </div>
                    )}
                    {salaryPreview.tds_deduction > 0 && (
                      <div className="flex justify-between">
                        <span>TDS</span>
                        <span>-{formatCurrency(salaryPreview.tds_deduction)}</span>
                      </div>
                    )}
                  </div>
                </details>
              )}

              <p className="text-xs text-slate-500 text-center italic">
                * This is an estimate. Final salary may vary based on pending attendance updates.
              </p>
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <Wallet className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Salary information not available</p>
              <p className="text-xs mt-1">Contact HR to set up your salary structure</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-sm">
        <CardContent className="p-6">
          <h3 className="font-semibold text-slate-900 dark:text-white mb-4">Quick Actions</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <a href="/work-hub/tasks" className="flex flex-col items-center gap-2 p-4 rounded-xl bg-indigo-50 dark:bg-indigo-900/20 hover:bg-indigo-100 dark:hover:bg-indigo-900/30 transition-colors cursor-pointer">
              <ListTodo className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
              <span className="text-sm font-medium text-indigo-700 dark:text-indigo-300">View Tasks</span>
            </a>
            <a href="/leads" className="flex flex-col items-center gap-2 p-4 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 transition-colors cursor-pointer">
              <Users className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
              <span className="text-sm font-medium text-emerald-700 dark:text-emerald-300">My Leads</span>
            </a>
            <a href="/calendar" className="flex flex-col items-center gap-2 p-4 rounded-xl bg-violet-50 dark:bg-violet-900/20 hover:bg-violet-100 dark:hover:bg-violet-900/30 transition-colors cursor-pointer">
              <Calendar className="w-6 h-6 text-violet-600 dark:text-violet-400" />
              <span className="text-sm font-medium text-violet-700 dark:text-violet-300">Calendar</span>
            </a>
            <a href="/contacts" className="flex flex-col items-center gap-2 p-4 rounded-xl bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors cursor-pointer">
              <FolderKanban className="w-6 h-6 text-amber-600 dark:text-amber-400" />
              <span className="text-sm font-medium text-amber-700 dark:text-amber-300">Contacts</span>
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EmployeeDashboard;
